import {
  type CryptoCurrency,
  type CryptoCurrencyDataSource,
  type FiatCurrency,
  setActionPrices,
} from '@0xtorch/core'
import {
  type AccountXrpl,
  getXrplSyncState,
  saveXrplActions,
  saveXrplSyncPage,
  setXrplIndexComplete,
} from '@pkg/database-portfolio'
import {
  type XrplTransaction,
  analyzeXrplTransaction,
  type createXrplRpcClient,
  createXrplSourceId,
  xrplTransactionSchema,
} from '@pkg/xrpl'
import { z } from 'zod'
import type { RunToDatabase } from './types'

/** 同じブラウザからの同期重複を防ぐ。 */
let analyzing = false

/**
 * Mainnet の履歴取得、metadata 解析、既存価格付与、成功分の保存を実行する。
 * @param params 全登録アカウント、対象 ID、DB、RPC client、既存価格基盤と進捗通知
 * @returns エラーを通知し、成功分と未完了状態を保持した時点で解決する Promise
 */
export const analyzeXrplAccounts = async (params: {
  readFromDatabase: RunToDatabase
  writeToDatabase: RunToDatabase
  accounts: readonly AccountXrpl[]
  targetAccountIds: ReadonlySet<number>
  client: ReturnType<typeof createXrplRpcClient>
  startTime: number
  fiat: FiatCurrency
  cryptoCurrencies?: readonly CryptoCurrency[]
  priceDataSource: CryptoCurrencyDataSource
  onProgress: (
    progress:
      | { status: 'check-account' | 'analyze'; progress: number }
      | undefined,
  ) => void
  onError: (error: unknown) => void
}): Promise<void> => {
  if (analyzing) return
  const targets = params.accounts.filter((account) =>
    params.targetAccountIds.has(account.id),
  )
  if (targets.length === 0) return
  analyzing = true
  try {
    for (const account of targets) {
      await params.writeToDatabase((database) =>
        setXrplIndexComplete({
          database,
          accountId: account.id,
          complete: false,
        }),
      )
    }
    for (const [position, account] of targets.entries()) {
      params.onProgress({
        status: 'check-account',
        progress: (position / targets.length) * 100,
      })
      await params.writeToDatabase((database) =>
        setXrplIndexComplete({
          database,
          accountId: account.id,
          complete: false,
        }),
      )
      try {
        const state = await params.readFromDatabase((database) =>
          getXrplSyncState({ database, accountId: account.id }),
        )
        const checkpoint =
          state.checkpoint?.startTime === params.startTime
            ? state.checkpoint
            : undefined
        const range = await params.client.getLedgerRange({
          startTime: params.startTime,
          ...(checkpoint === undefined
            ? {}
            : checkpoint.complete
              ? { ledgerMin: checkpoint.ledgerMax }
              : {
                  ledgerMin: checkpoint.ledgerMin,
                  ledgerMax: checkpoint.ledgerMax,
                }),
        })
        const marker =
          checkpoint?.complete === false ? checkpoint.marker : undefined
        await params.writeToDatabase((database) =>
          saveXrplSyncPage({
            database,
            accountId: account.id,
            transactions: [],
            checkpoint: {
              startTime: params.startTime,
              ledgerMin: range.min,
              ledgerMax: range.max,
              marker,
              complete: false,
            },
          }),
        )
        await params.client.listTransactions({
          address: account.xrplAddress,
          ledgerMin: range.min,
          ledgerMax: range.max,
          marker,
          onPage: async ({ transactions, nextMarker }) => {
            await params.writeToDatabase((database) =>
              saveXrplSyncPage({
                database,
                accountId: account.id,
                transactions: transactions.filter(
                  (transaction) =>
                    Date.parse(transaction.close_time_iso) >= params.startTime,
                ),
                checkpoint: {
                  startTime: params.startTime,
                  ledgerMin: range.min,
                  ledgerMax: range.max,
                  marker: nextMarker,
                  complete: nextMarker === undefined,
                },
              }),
            )
          },
        })
        await params.writeToDatabase((database) =>
          setXrplIndexComplete({
            database,
            accountId: account.id,
            complete: true,
          }),
        )
      } catch (error) {
        params.onError(error)
      }
    }
    const currencies = new Map(
      params.cryptoCurrencies?.map((currency) => [currency.id, currency]),
    )
    const pending = new Map<string, XrplTransaction>()
    for (const account of targets) {
      const state = await params.readFromDatabase((database) =>
        getXrplSyncState({ database, accountId: account.id }),
      )
      for (const row of state.pending)
        pending.set(
          row.sourceId,
          xrplTransactionSchema.parse(JSON.parse(row.raw)),
        )
    }
    const addresses = new Set(
      params.accounts.map((account) => account.xrplAddress),
    )
    const mptScales = new Map<string, number>()
    let position = 0
    for (const transaction of pending.values()) {
      params.onProgress({
        status: 'analyze',
        progress: (position++ / pending.size) * 100,
      })
      try {
        for (const entry of transaction.meta.AffectedNodes) {
          for (const value of Object.values(entry)) {
            const node = z
              .object({
                LedgerEntryType: z.string(),
                NewFields: z.record(z.unknown()).optional(),
                FinalFields: z.record(z.unknown()).optional(),
              })
              .parse(value)
            if (node.LedgerEntryType !== 'MPToken') continue
            const fields = node.FinalFields ?? node.NewFields
            if (!addresses.has(String(fields?.Account))) continue
            const id = z
              .string()
              .regex(/^[A-Fa-f0-9]{48}$/)
              .parse(fields?.MPTokenIssuanceID)
              .toUpperCase()
            if (!mptScales.has(id))
              mptScales.set(
                id,
                await params.client.getMptScale({
                  issuanceId: id,
                  ledgerIndex: transaction.ledger_index,
                }),
              )
          }
        }
        const actions = analyzeXrplTransaction({
          transaction,
          addresses,
          mptScales,
        })
        const priced = await setActionPrices({
          actions: actions.map((action) => ({
            ...action,
            transfers: action.transfers.map((transfer) => {
              if (!transfer.asset.id.startsWith('xrpl:iou:')) return transfer
              const coinId = currencies.get(transfer.asset.id)?.market
                ?.coingeckoId
              const coin =
                coinId === undefined ? undefined : currencies.get(coinId)
              return coin !== undefined && coin.market?.coingeckoId === coinId
                ? { ...transfer, asset: coin }
                : transfer
            }),
          })),
          fiat: params.fiat,
          dataSource: params.priceDataSource,
        })
        await params.writeToDatabase((database) =>
          saveXrplActions({
            database,
            source: {
              id: createXrplSourceId({ hash: transaction.hash }),
              type: 'xrpl',
              xrplHash: transaction.hash,
            },
            actions: priced,
          }),
        )
      } catch (error) {
        params.onError(error)
      }
    }
  } catch (error) {
    params.onError(error)
  } finally {
    analyzing = false
    params.onProgress(undefined)
  }
}
